<template>
  <div ref="simpleInputRef" class="tw-relative tw-overflow-hidden">
    <input
      v-if="type === 'input'"
      :id="id"
      v-model="note"
      :type="type"
      :name="name"
      :placeholder="placeholder"
      :required="required"
      :class="`tw-flex tw-w-full tw-items-start ${classStyle} ${
        hasError ? 'tw-border-red-500 tw-text-[red]' : ''
      }`"
      :disabled="disabled"
      @input="(e: any) => $emit('update:value', e.target.value)"
      @blur="$emit('on:blur', note)"
      @focus="$emit('on:focus')"
      @keyup.enter="$emit('on:enter')"
    />

    <textarea
      v-if="type === 'textarea'"
      :id="id"
      v-model="note"
      :type="type"
      :name="name"
      :placeholder="placeholder"
      :disabled="disabled"
      :required="required"
      :class="`tw-flex tw-w-full tw-h-[46px] tw-items-start ${classStyle} ${
        hasError ? 'tw-border-red-500 tw-text-[red]' : ''
      }`"
      @input="(e: any) => $emit('update:value', e.target.value)"
      @blur="$emit('on:blur')"
      @focus="$emit('on:focus')"
      @keyup.enter="$emit('on:enter')"
    />

    <div v-if="type === 'textarea'">
      <StrikesIcon
        v-if="value === '' || isShowIcon"
        class="tw-absolute tw-bottom-[6.93px] tw-right-[6.93px]"
      />
      <div
        v-else
        class="tw-absolute tw-bottom-[7.93px] tw-right-[6.93px] tw-cursor-pointer"
        @click="clickCheronIcon"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="6"
          height="9"
          viewBox="0 0 6 9"
          fill="none"
        >
          <path
            d="M1.2041 8.9143L5.87628 4.79289C5.91551 4.75577 5.94676 4.71104 5.96812 4.66144C5.98947 4.61184 6.00049 4.5584 6.00049 4.50439C6.00049 4.45038 5.98947 4.39694 5.96812 4.34734C5.94676 4.29774 5.91551 4.25301 5.87628 4.21589L1.2041 0.094479C1.16698 0.0615286 1.12369 0.036282 1.07674 0.0202021C1.02979 0.00412221 0.980107 -0.00246958 0.930586 0.000810113C0.880666 0.00487277 0.832068 0.0189023 0.787663 0.0420699C0.743259 0.0652375 0.703951 0.0970721 0.672061 0.135693L0.0913165 0.82884C0.0585649 0.866704 0.0337331 0.910746 0.0182889 0.958367C0.00284411 1.00599 -0.00289987 1.05622 0.00139501 1.1061C0.00498608 1.15574 0.0184324 1.20417 0.0409477 1.24856C0.0634635 1.29295 0.0945971 1.3324 0.13253 1.36462L3.6732 4.4407C3.69196 4.45964 3.70249 4.48522 3.70249 4.51188C3.70249 4.53854 3.69196 4.56413 3.6732 4.58307L0.132531 7.64416C0.0601858 7.70473 0.0133556 7.79036 0.0013956 7.88395C-0.00340948 7.93357 0.0017375 7.98366 0.0165352 8.03126C0.0313324 8.07887 0.0554823 8.12305 0.0875706 8.16121L0.672062 8.87309C0.703951 8.91171 0.743259 8.94354 0.787664 8.96671C0.832068 8.98988 0.880667 9.00391 0.930586 9.00797C0.980108 9.01125 1.02979 9.00466 1.07674 8.98858C1.12369 8.9725 1.16698 8.94725 1.2041 8.9143Z"
            fill="#a9a9a9"
          />
        </svg>
      </div>
    </div>

    <div v-if="type === 'html-input'">
      <WysiwygEditor
        v-if="showWysiwygEditor"
        :id="id"
        ref="wysiwygEditorRef"
        :model-value="note"
        :include-image="false"
        :is-disabled="disabled"
        :height="'38px'"
        :placeholder="placeholder"
        @update:value="onUpdateWysiwygEditor"
        @update:blur="$emit('on:blur', note)"
        @update:keydown="onkeydown"
        @update:focus="$emit('on:focus')"
        @on:ready="onUpdateWysiwygEditor(note)"
      />

      <!-- If length of value > max width => show 3 dots by input -->
      <input
        v-else
        :id="id"
        v-model="wysiwygNoteShown"
        :type="type"
        :name="name"
        :class="`tw-flex tw-w-full tw-items-start !tw-p-[6px] !tw-text-[13px] !tw-font-normal`"
        @focus="$emit('on:focus')"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import { onMounted, Ref, ref, watch } from "vue";
import StrikesIcon from "../svgs/strikesIcon.vue";
import WysiwygEditor from "./wysiwygEditor.vue";

const props = defineProps({
  type: {
    type: String,
    required: true,
  },
  classStyle: {
    type: String,
    required: false,
    default: "",
  },
  placeholder: {
    type: String,
    required: false,
    default: "",
  },
  id: {
    type: String,
    required: false,
    default: "",
  },
  name: {
    type: String,
    required: false,
    default: "",
  },
  required: {
    type: Boolean,
    required: false,
    default: false,
  },
  value: {
    type: String,
    required: false,
    default: "",
  },
  hasError: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  isShowIcon: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const note = ref(props.value);
const wysiwygEditorRef: Ref<any> = ref(null);
const simpleInputRef: Ref<any> = ref(null);
const wysiwygNoteShown = ref("");
const showWysiwygEditor = ref(true);
const wysiwygEditorMaxWidth = ref(0);

watch(
  () => props.value,
  (newValue) => {
    note.value = newValue;

    if (props.type === "html-input") {
      showWysiwygEditor.value = true;
      $emit("on:inputOverflow", false);

      handleShowWysiwygEditor(newValue);
      onUpdateWysiwygEditor(newValue);
    }
  },
);

const $emit = defineEmits([
  "update:value",
  "on:blur",
  "on:focus",
  "on:enter",
  "click:cheron",
  "on:inputOverflow",
]);

const clickCheronIcon = () => {
  $emit("click:cheron");
};

const handleWysiwygEditor = (value) => {
  const simpleInputWidth = simpleInputRef.value.scrollWidth || 0;
  const wysiwygEditorWidth =
    wysiwygEditorRef.value?.editableRef?.scrollWidth || 0;

  if (wysiwygEditorWidth === 0 || simpleInputWidth === 0) return;

  showWysiwygEditor.value = !(wysiwygEditorWidth > simpleInputWidth);
  $emit("on:inputOverflow", !showWysiwygEditor.value);

  if (!showWysiwygEditor.value && wysiwygEditorMaxWidth.value === 0)
    wysiwygEditorMaxWidth.value = value.length;
};

const onUpdateWysiwygEditor = async (value) => {
  value && handleWysiwygEditor(value);
  handleShowWysiwygEditor(value);

  await new Promise((resolve) => setTimeout(resolve, 30));
  $emit("update:value", value);
};

const onkeydown = (event) => {
  if (event.key === "Enter" || event.key === "ArrowDown") {
    event.preventDefault();
    event.stopPropagation();
    $emit("on:enter");
  }
};

const handleShowWysiwygEditor = (text: string) => {
  if (props.type !== "html-input") return;
  const temp = document.createElement("div");
  temp.innerHTML = text || "";
  wysiwygNoteShown.value = temp.textContent || temp.innerText || "";
};

onMounted(() => {
  handleShowWysiwygEditor(note.value);
});
</script>

<style scoped>
input,
textarea {
  display: flex;
  padding: 12px;
  align-items: flex-start;
  gap: 10px;
  flex: 1;
  border-radius: 6px;
  border: 1px solid #eff0f1;
  font-family: "Poppins";
  font-size: 11px;
  font-style: normal;
  font-weight: 500;
  line-height: 100%;
  color: #231f20;
  background-color: inherit;
  outline: none;
  background-color: #ffffff;
  min-height: 38px;
  min-width: 53px;
  height: 38px;
  padding-right: 25px;
  text-overflow: ellipsis;
  resize: none;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

input::placeholder,
textarea::placeholder {
  color: #b7b7b7;
}

textarea::-webkit-scrollbar {
  display: none;
}

textarea::-webkit-resizer {
  margin-right: 6.93px;
}

:deep(.ck.ck-reset_all) {
  display: none;
}

:deep(.ck .ck-content) {
  height: 38px;
  border-top-left-radius: 6px !important;
  border-top-right-radius: 6px !important;
  overflow-y: hidden;
}

:deep(.ck-editor__editable) {
  overflow-y: hidden !important;
  max-height: 100%; /* Hoặc chiều cao cố định */
}

:deep(.ck-content) {
  padding: 0px 6px !important;
}

:deep(.ck.ck-editor__editable_inline > :first-child) {
  margin-top: 8px !important;
  margin-bottom: 40px !important;
  gap: 100px;
  display: flex;
  flex-direction: column;
}
:deep(.ck-editor__editable) {
  white-space: nowrap;
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;
}
</style>
